import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Bell,
  User,
  FilePen,
  UserRound,
  Fingerprint,
  Link,
  FileText,
  Building2,
  Search,
  Layers,
  IdCard,
  Compass,
  Settings,
  Lock,
  Smartphone,
  FolderOpen,
  ClipboardList,
  ShieldCheck,
  X,
  UserPlus,
  LogOut
} from 'lucide-react';
import { apiEndpoint } from '../config/api';

const ME_API_URL =
  process.env.REACT_APP_ME_API_URL ||
  apiEndpoint('api/me.php');

const NOTIFICATIONS_API_URL =
  process.env.REACT_APP_NOTIFICATIONS_API_URL ||
  apiEndpoint('api/notifications.php');

const HomePage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
 

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState('');
  const [userInfo, setUserInfo] = useState({
    taxCode: '',
    businessName: '',
    phone: ''
  });

  // Lấy thông tin người dùng trực tiếp từ API /api/me.php
  useEffect(() => {
    const controller = new AbortController();

    const loadUserInfo = async () => {
      try {
        const token =
          window.localStorage.getItem('demo_auth_token') ||
          window.sessionStorage.getItem('demo_auth_token');

        if (!token) {
          navigate('/login', { replace: true });
          return;
        }

        const response = await fetch(
          ME_API_URL,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
            cache: 'no-store',
            signal: controller.signal,
          }
        );

        const result = await response.json().catch(() => null);

        if (!response.ok || !result?.success || !result?.user) {
          if (response.status === 401) {
            window.localStorage.removeItem('demo_auth_token');
            window.localStorage.removeItem('demo_user');
            window.sessionStorage.removeItem('demo_auth_token');
            window.sessionStorage.removeItem('demo_user');
            navigate('/login', { replace: true });
            return;
          }

          throw new Error(
            result?.message || 'Không thể tải thông tin người dùng.'
          );
        }

        setUserInfo({
          taxCode: result.user.taxCode || '',
          businessName: result.user.company || '',
          phone: result.user.phone || '',
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Không thể tải thông tin người dùng:', error);
        }
      }
    };

    loadUserInfo();

    return () => controller.abort();
  }, [navigate]);
  
  const getAuthToken = () =>
    window.localStorage.getItem('demo_auth_token') ||
    window.sessionStorage.getItem('demo_auth_token');

  const loadNotifications = async ({ silent = false } = {}) => {
    const token = getAuthToken();

    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    if (!silent) {
      setIsLoadingNotifications(true);
    }

    try {
      const response = await fetch(NOTIFICATIONS_API_URL, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message || 'Không thể tải danh sách thông báo.'
        );
      }

      setNotifications(
        Array.isArray(result.notifications) ? result.notifications : []
      );

      setUnreadCount(
        Number.isFinite(Number(result.unreadCount))
          ? Number(result.unreadCount)
          : 0
      );

      setNotificationError('');
    } catch (error) {
      console.error('Không thể tải thông báo:', error);

      if (!silent) {
        setNotificationError(
          error.message || 'Không thể tải danh sách thông báo.'
        );
      }
    } finally {
      if (!silent) {
        setIsLoadingNotifications(false);
      }
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    const token = getAuthToken();

    if (!token || !notificationId) return;

    try {
      const response = await fetch(NOTIFICATIONS_API_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'mark_read',
          notification_id: notificationId,
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message || 'Không thể đánh dấu thông báo đã đọc.'
        );
      }

      setNotifications((current) =>
        current.map((item) =>
          Number(item.id) === Number(notificationId)
            ? { ...item, isRead: true }
            : item
        )
      );

      setUnreadCount((current) => Math.max(0, current - 1));
    } catch (error) {
      console.error('Không thể đánh dấu đã đọc:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    const token = getAuthToken();

    if (!token || unreadCount <= 0) return;

    try {
      const response = await fetch(NOTIFICATIONS_API_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'mark_all_read',
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message || 'Không thể đánh dấu tất cả đã đọc.'
        );
      }

      setNotifications((current) =>
        current.map((item) => ({ ...item, isRead: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Không thể đánh dấu tất cả đã đọc:', error);
    }
  };

  useEffect(() => {
    loadNotifications();

    const intervalId = window.setInterval(() => {
      loadNotifications({ silent: true });
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!isNotificationOpen) return;
    loadNotifications();
  }, [isNotificationOpen]);

  const handleQuickActionClick = (index) => {
    if (index === 0) {
      // Navigate to UpdateHoSoPage when clicking "Cập nhật hồ sơ"
      navigate('/profile');
    } else if (index === 1) {
      // Navigate to IdentificationPage when clicking "Định danh"
      navigate('/identification');
    } else if (index === 2){
         navigate('/qrcode');
    }
    // Add more navigation logic for other actions if needed
  };

  const handleServiceClick = (index) => {
    if (index === 4) {
      // Navigate to BankRegistrationPage when clicking "Đăng ký tài khoản doanh nghiệp trực tuyến"
      navigate('/bank-registration');
    }
    // Add more navigation logic for other services if needed
  };

  const quickActions = [
    { icon: FilePen, label: 'Cập nhật hồ sơ' },
    { icon: UserRound, label: 'Định danh' },
    { icon: Fingerprint, label: 'Mã định danh' },
    { icon: Link, label: 'Liên kết tài khoản' },
  ];
  const services = [
    { icon: FileText, label: 'Đăng ký thuế' },
    { icon: Building2, label: 'Hỗ trợ quyết toán thuế TNCN' },
    { icon: Search, label: 'Tra cứu hồ sơ khai thuế' },
    { icon: Layers, label: 'Nhóm chức năng nộp thuế' },
    { icon: IdCard, label: 'Đăng ký tài khoản doanh nghiệp trực tuyến' },
    { icon: Bell, label: 'Tra cứu thông báo' },
    { icon: Compass, label: 'Tiện ích' },
    { icon: Settings, label: 'Hỗ trợ' },
    { icon: User, label: 'Thiết lập cá nhân' },
    { icon: Lock, label: 'Đổi mật khẩu' },
    { icon: Smartphone, label: 'Đăng nhập bằng vân tay' },
    { icon: Compass, label: 'Khám phá' },
    { icon: Fingerprint, label: 'Định danh' },
    { icon: FolderOpen, label: 'Giấy tờ' },
    { icon: ClipboardList, label: 'Tích hợp thông tin' },
    { icon: User, label: 'Người phụ thuộc' },
    { icon: User, label: 'Cá nhân' },
    { icon: ShieldCheck, label: 'Bảo hiểm xã hội' },
  ];

  const menuItems = [
    { icon: FilePen, label: 'Định danh' },
    { icon: UserPlus, label: 'Liên kết tài khoản' },
    { icon: Compass, label: 'Khám phá' },
    { icon: Lock, label: 'Đổi mật khẩu' },
  ];

  return (
    <div className="relative min-h-screen bg-background pb-2">
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none"
        style={{ 
          backgroundImage: `url(${require('../assets/background-C60agDj0.png')})`,
          opacity: 2
        }}
      />

      <div className="min-h-screen bg-transparent pb-6 relative z-10">
        {/* Header */}
        <header className="relative bg-gradient-primary">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2 w-12">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-lg text-primary-foreground hover:bg-white/10 transition-colors"
                aria-label="Menu"
              >
                <Menu size={32} />
              </button>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-white text-center">
                <div className="text-2xl font-bold">THUẾ ĐIỆN TỬ</div>
              </div>
            </div>
            <div className="flex items-center gap-1 w-12 justify-end">
            <div className="relative">
  <button
    onClick={() => {
      setIsSidebarOpen(false);
      setIsNotificationOpen(true);
    }}
    className="p-2 rounded-lg text-primary-foreground hover:bg-white/10 transition-colors"
    aria-label="Thông báo"
  >
    <Bell size={32} />
  </button>

  {unreadCount > 0 && (
    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
      {unreadCount > 99 ? '99+' : unreadCount}
    </span>
  )}
</div>
            </div>
          </div>
        </header>

        {/* User Profile Card */}
        <div className="relative">
          <div className="relative z-10 mx-4 mt-4">
            <div 
              className="bg-card rounded-2xl px-2 py-3 shadow-lg flex items-center gap-2 relative overflow-hidden"
              style={{
                backgroundImage: `url(${require('../assets/trongdong-B7jFCpOw.png')})`,
                backgroundPosition: 'center center',
                backgroundSize: 'cover'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-red-50/40 to-red-100/40" />
              <div className="relative z-10 w-14 h-14 rounded-full bg-transparent flex items-center justify-center border-2 border-red-800">
                <User size={50} className="text-black" />
              </div>
              <div className="relative z-10 flex-1">
                <p className="text-md text-black font-bold">
                  Mã số thuế: <span className="font-bold text-foreground">
                    {userInfo.taxCode || 'Chưa có MST'}
                  </span>
                </p>
                <p className="text-md text-black font-bold">
                  Doanh nghiệp: <span className="font-bold text-foreground">
                    {userInfo.businessName || 'Chưa cập nhật thông tin'}
                  </span>
                </p>
                <p className="text-primary text-black font-bold">
                  {userInfo.phone || 'Chưa cập nhật số điện thoại'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="px-4 mt-6 relative">
          <div 
            className="bg-card rounded-2xl p-4 shadow-lg flex-col items-center gap-4 relative overflow-hidden"
            style={{
              backgroundImage: `url(${require('../assets/trong-CK13KxDf.png')})`,
              backgroundPosition: 'center center',
              backgroundSize: 'cover'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/70 to-white opacity-70" />
            <h2 className="relative z-10 text-[16px] font-bold text-foreground mb-3">
              Chức năng hay dùng
            </h2>
            <div className="relative z-10 grid grid-cols-4 gap-3">
              {quickActions.map((action, index) => (
                <button 
                  key={index} 
                  className="flex flex-col items-center min-h-[80px]"
                  onClick={() => handleQuickActionClick(index)}
                >
                  <div className="w-10 h-8 mx-auto rounded-xl bg-primary flex items-center justify-center mb-2 flex-shrink-0">
                    <action.icon size={20} className="text-white" />
                  </div>
                  <p className="text-[14px] text-center text-foreground font-sm leading-tight flex items-start justify-center line-clamp-2 pt-1">
                    {action.label}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="px-4 mt-5">
          <div 
            className="relative bg-card rounded-2xl p-4 border border-border overflow-hidden"
            style={{
              backgroundImage: `url(${require('../assets/trong-CK13KxDf.png')})`,
              backgroundPosition: 'center center',
              backgroundSize: 'cover'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/30 to-white opacity-70" />
            <h2 className="relative z-10 text-[17px] font-bold text-foreground mb-3">
              Danh sách nhóm dịch vụ
            </h2>
            <div className="relative z-10 grid grid-cols-3 gap-4">
              {services.map((service, index) => (
                <button
                  key={index}
                  type="button"
                  className="flex flex-col items-center gap-2 p-2 bg-card rounded-xl hover:bg-muted/50 transition-colors min-h-[100px]"
                  onClick={() => handleServiceClick(index)}
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <service.icon size={18} className="text-primary" />
                  </div>
                  <span className="text-[14px] text-center text-foreground font-sm leading-tight flex items-center justify-center line-clamp-2">
                    {service.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
{isNotificationOpen && (
  <>
    <div
      className="fixed inset-0 bg-black/60 z-[90]"
      onClick={() => setIsNotificationOpen(false)}
    />

    <aside className="fixed inset-0 bg-white z-[100]">
      <div className="bg-gradient-primary px-4 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-white text-xl font-bold">
            Thông báo
          </h2>
          {unreadCount > 0 && (
            <p className="text-white/80 text-sm">
              {unreadCount} thông báo chưa đọc
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllNotificationsAsRead}
              className="text-white text-sm underline underline-offset-2"
            >
              Đọc tất cả
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsNotificationOpen(false)}
            className="text-white"
            aria-label="Đóng thông báo"
          >
            <X size={30} />
          </button>
        </div>
      </div>

      <div className="p-4 overflow-y-auto h-[calc(100vh-72px)]">
        {isLoadingNotifications ? (
          <div className="text-center text-gray-500 mt-10">
            Đang tải thông báo...
          </div>
        ) : notificationError ? (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-center text-red-700">
            <p>{notificationError}</p>
            <button
              type="button"
              onClick={() => loadNotifications()}
              className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white"
            >
              Thử lại
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center text-gray-500 mt-10">
            Không có thông báo
          </div>
        ) : (
          notifications.map((item) => (
            <button
              type="button"
              key={item.id}
              onClick={() => {
                if (!item.isRead) {
                  markNotificationAsRead(item.id);
                }
              }}
              className={`mb-4 block w-full rounded-xl border p-4 text-left shadow-sm transition ${
                item.isRead
                  ? 'border-gray-200 bg-white'
                  : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-bold text-red-600 text-lg">
                  {item.title || 'Thông báo'}
                </div>

                {!item.isRead && (
                  <span className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-600" />
                )}
              </div>

              <div className="mt-2 text-gray-700 whitespace-pre-wrap break-words">
                {item.content || 'Chưa có nội dung'}
              </div>

              {(item.publishedAt || item.createdAt) && (
                <div className="mt-2 text-xs text-gray-400">
                  {item.publishedAt || item.createdAt}
                </div>
              )}
            </button>
          ))
        )}
      </div>
    </aside>
  </>
)}
      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 z-50 transition-transform duration-300 ease-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="bg-gradient-primary relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/5 rounded-full" />
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="absolute top-4 right-4 p-2 text-primary-foreground hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
          <div className="flex flex-col items-center pt-8 pb-4">
            <div className="text-white text-3xl font-bold mb-2">THUẾ ĐIỆN TỬ</div>
          </div>
          <div className="bg-primary/80 py-3 text-center">
            <span className="text-primary-foreground font-medium">Xin chào</span>
          </div>
        </div>
        <div className="bg-card h-full pt-4 px-3">
          {menuItems.map((item, index) => (
            <button key={index} className="menu-item w-full text-left hover:bg-muted mb-2 p-2 rounded-lg flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <item.icon size={18} className="text-muted-foreground" />
              </div>
              <span className="text-foreground font-medium">{item.label}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              window.localStorage.removeItem('demo_auth_token');
              window.localStorage.removeItem('demo_user');
              window.sessionStorage.removeItem('demo_auth_token');
              window.sessionStorage.removeItem('demo_user');
              navigate('/login', { replace: true });
            }}
            className="mt-6 w-full py-3 rounded-full bg-yellow-400 text-foreground font-semibold hover:bg-yellow-500 transition-colors flex items-center justify-center gap-2"
          >
            <LogOut size={18} />
            Đăng Xuất
          </button>
        </div>
      </aside>
    </div>
  );
};

export default HomePage;
